import { useEffect } from 'react'

/**
 * Pile d'overlays (LOT 5.5) — un seul arbitre pour Escape et le scroll.
 *
 * Chaque surface flottante (drawer, dialog, palette, popover) s'enregistre
 * à l'ouverture. Escape ferme UNIQUEMENT la plus haute — plus jamais trois
 * composants qui réagissent en même temps. Le verrou de scroll est compté :
 * il tient tant qu'au moins un overlay bloquant est ouvert.
 */

interface OverlayEntry { id: number; close: () => void; lockScroll: boolean }

let stack: OverlayEntry[] = []
let nextId = 1
let keydownBound = false

function syncScrollLock(): void {
  const locked = stack.some((o) => o.lockScroll)
  document.body.style.overflow = locked ? 'hidden' : ''
}

function onKeydown(e: KeyboardEvent): void {
  if (e.key !== 'Escape' || stack.length === 0) return
  e.stopPropagation()
  stack[stack.length - 1]!.close()
}

export function pushOverlay(close: () => void, opts: { lockScroll?: boolean } = {}): () => void {
  const entry: OverlayEntry = { id: nextId++, close, lockScroll: opts.lockScroll ?? false }
  stack.push(entry)
  if (!keydownBound) { document.addEventListener('keydown', onKeydown); keydownBound = true }
  syncScrollLock()
  return () => {
    stack = stack.filter((o) => o.id !== entry.id)
    syncScrollLock()
  }
}

/** Hook : enregistre l'overlay tant que `open` est vrai. */
export function useOverlay(open: boolean, close: () => void, opts: { lockScroll?: boolean } = {}): void {
  useEffect(() => {
    if (!open) return
    return pushOverlay(close, opts)
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps
}
